import {
  COMPANY_ADDRESS_FULL,
  COMPANY_INITIALS,
  COMPANY_LOCATION,
  COMPANY_NAME,
  COMPANY_REGION,
  COMPANY_SHORT,
  HERO_IMAGE_URL,
  MAP_URL,
  CARE_PILLARS,
  PROFILE_FACTS,
  PUBLIC_INFO,
} from './constants';

function Header() {
  const navItems = [
    { label: 'About', href: '#about' },
    { label: 'Care', href: '#care' },
    { label: 'Visit', href: '#visit' },
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-line/70 bg-cream/85 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-4">
        <a href="#top" className="flex min-w-0 items-center gap-3" aria-label={`${COMPANY_SHORT} home`}>
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-plum text-sm font-black text-petal shadow-ring">
            {COMPANY_INITIALS}
          </span>
          <span className="font-display truncate text-xl font-semibold text-plum sm:text-2xl">
            {COMPANY_SHORT}
          </span>
        </a>
        <nav className="hidden items-center gap-2 md:flex" aria-label="Primary">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="rounded-pill px-4 py-2 text-sm font-semibold text-ink/70 transition hover:bg-petal hover:text-plum"
            >
              {item.label}
            </a>
          ))}
        </nav>
        <a
          href="#visit"
          className="rounded-pill bg-plum px-5 py-2.5 text-sm font-semibold text-petal transition hover:bg-rose"
        >
          Find us
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-cream bg-grain">
      <div className="pointer-events-none absolute -left-32 top-10 h-80 w-80 rounded-full bg-blush blur-3xl opacity-70" />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-petal blur-3xl opacity-80" />
      <div className="relative mx-auto max-w-5xl px-6 pt-20 pb-24 text-center lg:pt-28 lg:pb-32">
        <span className="inline-flex items-center gap-2 rounded-pill border border-rose/30 bg-petal/70 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.22em] text-rose">
          <span className="h-1.5 w-1.5 rounded-full bg-rose" />
          Public Profile · {COMPANY_REGION}
        </span>
        <h1 className="font-display mt-7 text-5xl font-semibold leading-[1.05] tracking-tight text-plum sm:text-6xl lg:text-7xl">
          {COMPANY_NAME}
        </h1>
        <p className="mx-auto mt-7 max-w-2xl text-lg leading-8 text-ink/70">
          A calm, modern public profile for a women&apos;s health organization based in {COMPANY_LOCATION}. The page
          presents only directory-safe information — supportive in tone and unhurried in design.
        </p>
        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href="#care"
            className="rounded-pill bg-plum px-7 py-3.5 font-semibold text-petal shadow-soft transition hover:bg-rose"
          >
            Our approach
          </a>
          <a
            href="#visit"
            className="rounded-pill border border-plum/25 bg-white px-7 py-3.5 font-semibold text-plum transition hover:border-plum hover:bg-petal"
          >
            See address
          </a>
        </div>

        <div className="mx-auto mt-16 grid max-w-4xl gap-4 sm:grid-cols-3">
          {PROFILE_FACTS.map((fact) => (
            <div
              key={fact.label}
              className="rounded-3xl border border-line/80 bg-white/70 px-5 py-5 text-left backdrop-blur"
            >
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-rose">{fact.label}</p>
              <p className="font-display mt-2 text-xl text-plum">{fact.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  return (
    <section id="about" className="bg-white">
      <div className="mx-auto grid max-w-6xl gap-12 px-6 py-20 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
        <div className="relative">
          <div className="absolute -left-3 -top-3 h-full w-full rounded-[2.25rem] bg-blush" aria-hidden />
          <div className="relative overflow-hidden rounded-[2.25rem] border border-line bg-plum shadow-soft">
            <img
              src={HERO_IMAGE_URL}
              alt="Soft floral arrangement representing women's wellness"
              className="h-[460px] w-full object-cover opacity-95"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-plum/85 via-plum/10 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-7 text-petal">
              <p className="text-[11px] font-black uppercase tracking-[0.24em] text-petal/70">Editorial Image</p>
              <p className="font-display mt-1 text-2xl">A profile rooted in care</p>
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs font-black uppercase tracking-[0.22em] text-rose">About the profile</p>
          <h2 className="font-display mt-3 text-4xl text-plum sm:text-5xl">
            A quiet introduction to {COMPANY_SHORT}.
          </h2>
          <p className="mt-5 leading-8 text-ink/75">
            This page exists as a public-safe directory profile. It avoids clinical claims, staff rosters, or service
            inventories that aren&apos;t already part of a verified public record — keeping only what a community
            member would expect to find on a calm, modern profile page.
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {PUBLIC_INFO.map((item) => (
              <div key={item.label} className="rounded-2xl border border-line bg-petal/40 px-5 py-4">
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-rose">{item.label}</p>
                <p className="mt-1.5 font-semibold text-plum">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CareSection() {
  return (
    <section id="care" className="bg-petal/60">
      <div className="mx-auto max-w-6xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-rose">Our character</p>
          <h2 className="font-display mt-3 text-4xl text-plum sm:text-5xl">Care, presented gently.</h2>
          <p className="mt-5 leading-8 text-ink/70">
            The page borrows from contemporary women&apos;s health editorial design: soft palette, generous spacing,
            serif display type, and rounded forms — built to feel calm rather than commercial.
          </p>
        </div>

        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {CARE_PILLARS.map((pillar) => (
            <article
              key={pillar.title}
              className="group flex h-full flex-col rounded-3xl border border-line/80 bg-white p-7 transition hover:-translate-y-1 hover:border-rose/50 hover:shadow-soft"
            >
              <span
                aria-hidden
                className="font-display grid h-12 w-12 place-items-center rounded-full bg-plum text-2xl text-petal transition group-hover:bg-rose"
              >
                {pillar.icon}
              </span>
              <h3 className="font-display mt-6 text-2xl text-plum">{pillar.title}</h3>
              <p className="mt-3 leading-7 text-ink/70">{pillar.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function VisitSection() {
  return (
    <section id="visit" className="relative overflow-hidden bg-plum text-petal">
      <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-rose/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-sage/20 blur-3xl" />
      <div className="relative mx-auto grid max-w-6xl gap-12 px-6 py-20 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.22em] text-blush">Visit</p>
          <h2 className="font-display mt-3 text-4xl text-petal sm:text-5xl">{COMPANY_LOCATION}</h2>
          <p className="mt-5 max-w-md leading-8 text-petal/75">
            Use the address below for directory listings, mail, and public correspondence. The location anchors the
            profile in {COMPANY_REGION}.
          </p>
        </div>

        <div className="relative rounded-[2rem] border border-petal/15 bg-white/8 p-8 backdrop-blur">
          <div className="absolute -top-4 left-8 inline-flex items-center gap-2 rounded-pill bg-rose px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-petal">
            Organization Address
          </div>
          <p className="font-display mt-3 text-3xl leading-snug text-petal sm:text-4xl">
            {COMPANY_ADDRESS_FULL}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href={MAP_URL}
              target="_blank"
              rel="noreferrer"
              className="rounded-pill bg-petal px-6 py-3 font-semibold text-plum transition hover:bg-blush"
            >
              Open in Google Maps
            </a>
            <a
              href="#top"
              className="rounded-pill border border-petal/30 px-6 py-3 font-semibold text-petal transition hover:bg-petal/10"
            >
              Back to top
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-cream">
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-10 text-sm text-ink/65 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-plum text-xs font-black text-petal">
            {COMPANY_INITIALS}
          </span>
          <p className="font-display text-lg text-plum">{COMPANY_SHORT}</p>
        </div>
        <p>&copy; {new Date().getFullYear()} {COMPANY_NAME}</p>
        <p>{COMPANY_LOCATION}</p>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div id="top" className="min-h-screen bg-cream text-ink">
      <a
        href="#about"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-pill focus:bg-plum focus:px-4 focus:py-2 focus:text-petal"
      >
        Skip to content
      </a>
      <Header />
      <main>
        <Hero />
        <AboutSection />
        <CareSection />
        <VisitSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
